import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SharedSurveyReport, surveyHelper, SurveyReportBuilder, SurveyReportDataSet } from '../../../../amorg-common';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { H1, H2 } from '../../../../designSystem/typography/Heading';
import { NoNavigationPageWrapper } from '../../../../designSystem/layouts/pageWrappers/NoNavigationPageWrapper';
import { getSurveyReportFromLinkId } from '../../../../api/surveyClientApi';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { ToolBarButton, ToolBarContainer } from '../../../../designSystem/compositeComponents/ButtonToolBar';
import { Divider } from '../../../../designSystem/Divider';
import { useReactToPrint } from 'react-to-print';
import PrintSurveyReportPage from '../../../_components/PrintSurveyReportPage';
import { PageHeader } from '../../../../designSystem/PageHeader';
import { SingleColumnLayout } from '../../../../designSystem/layouts/SingleColumnLayout';
import { PublicPageProvider } from '../../../_contexts/PublicPageContext';
import { PrintWrapper } from '../../../_components/PrintWrapper';
import { SurveyReportPanel } from '../../../workplaceassessments/[activityId]/_components/tabs/report/_components/SurveyReportPanel';
import { ReadOnlySegmentPanel } from '../../../workplaceassessments/[activityId]/_components/tabs/_components/ReadOnlySegmentPanel';
import { parseSiteScopedRouteParam } from '../../../../utils/parseSiteScopedRouteParam';


type PageState = {
    publicReport: SharedSurveyReport | null,
    loading: boolean
};
export function SurveyReportPublicPage(): JSX.Element {

    const params = useParams<{ linkIdSiteId: string }>();
    const linkRouteParams = parseSiteScopedRouteParam(params.linkIdSiteId);

    const [state, setState] = useState<PageState>({
        publicReport: null,
        loading: true
    });

    useEffect(() => {
        const getData = async () => {
            const publicReport = await getSurveyReportFromLinkId(linkRouteParams.siteId, linkRouteParams.value);
            const newState = {
                publicReport,
                loading: false
            };
            setState(newState);
        };
        getData();
    }, []);

    const hasQuestions = !state.loading && state.publicReport && surveyHelper.getAllSurveyQuestions(state.publicReport.survey).length > 0;

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: "Rapport"
    });

    const data:SurveyReportDataSet | null = React.useMemo(() => {
        if(!state.publicReport)
            return null;

        return SurveyReportBuilder.getDataFromSingleQuestionSummaryList(state.publicReport.survey, state.publicReport.data);
    }, [state.publicReport]);

    return (
        <PublicPageProvider>
        <NoNavigationPageWrapper>

            <SingleColumnLayout
                noDivider
                headerContainer={
                    <>
                        {state.loading &&
                            <LoadingPageOverlay loading={state.loading} />
                        }

                        {!state.loading && state.publicReport && data &&
                            <>
                            <PageHeader headerStyle='primary-black'
                                headerContentsOverride={
                                    <div>
                                        <H1 color="gray">Rapport</H1>
                                        <H2>{state.publicReport.survey.title}</H2>
                                    </div>
                                }
                                buttons={
                                    <ToolBarContainer>
                                        <ToolBarButton
                                            icon='interface_actions_print'
                                            tooltip= 'Print'
                                            variant= 'text'
                                            onClick={printFn} />

                                        <PrintWrapper innerRef={printRef}>
                                            <PrintSurveyReportPage
                                                survey={state.publicReport.survey}
                                                data={data}
                                                segmentFilter={state.publicReport.segmentFilter} />
                                        </PrintWrapper>
                                    </ToolBarContainer>
                                } />
                            </>
                        }
                    </>
                    
                }

                contentContainer={
                    <>
                    <Flex flexDirection='column' gap={16}>
                        {!state.loading && state.publicReport === null &&
                            <p>Det valgte link er ugyldigt, eller ikke længere tilgængeligt. Kontakt din arbejdsmiljøgruppe.</p>
                        }
                        {!state.loading && state.publicReport && hasQuestions &&
                        <Flex flexDirection='column' gap={16}>
                            <H2>Segmenter</H2>
                            <ReadOnlySegmentPanel segmentLists={state.publicReport.segmentFilter} lockedView='pills' />
                        </Flex>
                        }
                    </Flex>
                    
                    <Divider />

                    <Flex flexDirection='column' gap={16}>

                        {state.loading &&
                            <LoadingPageOverlay loading={state.loading} />
                        }
                        {!state.loading && state.publicReport && !hasQuestions &&
                            <p>Spørgeskemaet indeholder ikke spørgsmål</p>
                        }

                        {!state.loading && state.publicReport && hasQuestions && data &&
                            <>
                                <SurveyReportPanel
                                    siteId={linkRouteParams.siteId}
                                    survey={state.publicReport.survey}
                                    data={data}
                                    segments={state.publicReport.segmentFilter}
                                    showComments={false} />
                            </>
                        }

                    </Flex>
                    </>
                } />

        </NoNavigationPageWrapper>
        </PublicPageProvider>
    );
}